export const DEFAULT_NFC_FONT = '"Futura XBlkCn BT Extra Black", Nunito, sans-serif'

export const DEFAULT_CARD_FONT = "'Nunito', sans-serif"

export const CARD_FONT_OPTIONS = [
    { label: 'Futura Extra Black', value: DEFAULT_NFC_FONT },
    { label: 'Nunito', value: DEFAULT_CARD_FONT },
    { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
    { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
    { label: 'Trebuchet MS', value: "'Trebuchet MS', Helvetica, sans-serif" },
    { label: 'Georgia', value: "Georgia, 'Times New Roman', serif" },
    { label: 'Times New Roman', value: "'Times New Roman', Times, serif" },
    { label: 'Courier New', value: "'Courier New', Courier, monospace" },
]

export const DEFAULT_NFC_TYPOGRAPHY = {
    font_family: DEFAULT_NFC_FONT,
    font_size: 36,
    font_bold: false,
    font_italic: true,
    font_color: '#ffffff',
    text_shadow: true,
    text_shadow_blur: 7,
}

export const DEFAULT_BUSINESS_CARD_TYPOGRAPHY = {
    font_family: DEFAULT_CARD_FONT,
    font_size: 16,
    font_bold: true,
    font_italic: false,
    font_color: '#000000',
    text_shadow: false,
    text_shadow_blur: 7,
}

export const CARD_COLOR_PRESET_GROUPS = [
    {
        label: 'Neutrals',
        colors: [
            { label: 'White', value: '#ffffff' },
            { label: 'Pearl', value: '#f8fafc' },
            { label: 'Silver', value: '#cbd5e1' },
            { label: 'Slate', value: '#64748b' },
            { label: 'Charcoal', value: '#334155' },
            { label: 'Black', value: '#000000' },
        ],
    },
    {
        label: 'Warm',
        colors: [
            { label: 'Cream', value: '#fef3c7' },
            { label: 'Gold', value: '#fbbf24' },
            { label: 'Amber', value: '#f59e0b' },
            { label: 'Orange', value: '#f97316' },
            { label: 'Coral', value: '#fb7185' },
            { label: 'Red', value: '#ef4444' },
        ],
    },
    {
        label: 'Cool',
        colors: [
            { label: 'Mint', value: '#6ee7b7' },
            { label: 'Emerald', value: '#10b981' },
            { label: 'Teal', value: '#14b8a6' },
            { label: 'Cyan', value: '#06b6d4' },
            { label: 'Sky', value: '#38bdf8' },
            { label: 'Blue', value: '#3b82f6' },
        ],
    },
    {
        label: 'Bold',
        colors: [
            { label: 'Indigo', value: '#6366f1' },
            { label: 'Violet', value: '#8b5cf6' },
            { label: 'Purple', value: '#a855f7' },
            { label: 'Fuchsia', value: '#d946ef' },
            { label: 'Pink', value: '#ec4899' },
            { label: 'Lime', value: '#84cc16' },
        ],
    },
]

export const NFC_NAME_COLOR_PRESET_GROUPS = CARD_COLOR_PRESET_GROUPS

export const NFC_NAME_COLOR_PRESETS = CARD_COLOR_PRESET_GROUPS.flatMap((group) => group.colors)
export const CARD_COLOR_PRESETS = NFC_NAME_COLOR_PRESETS

export function isLightHexColor(value) {
    const hex = normalizeHexColor(value).slice(1)
    const red = parseInt(hex.slice(0, 2), 16)
    const green = parseInt(hex.slice(2, 4), 16)
    const blue = parseInt(hex.slice(4, 6), 16)
    const luminance = (red * 299 + green * 587 + blue * 114) / 1000

    return luminance > 186
}

export function normalizeHexColor(value, fallback = DEFAULT_NFC_TYPOGRAPHY.font_color) {
    if (!value || typeof value !== 'string') {
        return fallback
    }

    let hex = value.trim().replace(/^#/, '')
    if (/^[0-9a-fA-F]{3}$/.test(hex)) {
        hex = hex.split('').map((char) => char + char).join('')
    }

    if (/^[0-9a-fA-F]{6}$/.test(hex)) {
        return `#${hex.toLowerCase()}`
    }

    return fallback
}

export function buildCardTextShadow(text_shadow, text_shadow_blur) {
    if (!text_shadow) {
        return 'none'
    }

    const shadow = `0 0 ${text_shadow_blur}px #000000`
    return `${shadow}, ${shadow}, ${shadow}, ${shadow}`
}

export function buildCardTextStyle({
    font_family = DEFAULT_CARD_FONT,
    font_size = DEFAULT_BUSINESS_CARD_TYPOGRAPHY.font_size,
    font_bold = DEFAULT_BUSINESS_CARD_TYPOGRAPHY.font_bold,
    font_italic = DEFAULT_BUSINESS_CARD_TYPOGRAPHY.font_italic,
    font_color = DEFAULT_BUSINESS_CARD_TYPOGRAPHY.font_color,
    text_shadow = DEFAULT_BUSINESS_CARD_TYPOGRAPHY.text_shadow,
    text_shadow_blur = DEFAULT_BUSINESS_CARD_TYPOGRAPHY.text_shadow_blur,
} = {}) {
    const color = normalizeHexColor(font_color, font_color)

    return {
        fontFamily: font_family,
        fontSize: `${font_size}px`,
        fontWeight: font_bold ? '700' : '400',
        fontStyle: font_italic ? 'italic' : 'normal',
        margin: '0',
        padding: '0',
        lineHeight: '1.05',
        color,
        textShadow: buildCardTextShadow(text_shadow, text_shadow_blur),
    }
}

export function buildCardTextCssVars(options = {}) {
    const style = buildCardTextStyle(options)

    return {
        '--ps-card-text-color': style.color,
        '--ps-card-text-size': style.fontSize,
        '--ps-card-text-font': style.fontFamily,
        '--ps-card-text-weight': style.fontWeight,
        '--ps-card-text-style': style.fontStyle,
        '--ps-card-text-shadow': style.textShadow,
    }
}

export function buildNfcNameTextStyle(options = {}) {
    return buildCardTextStyle({
        font_family: DEFAULT_NFC_FONT,
        font_size: DEFAULT_NFC_TYPOGRAPHY.font_size,
        font_bold: DEFAULT_NFC_TYPOGRAPHY.font_bold,
        font_italic: DEFAULT_NFC_TYPOGRAPHY.font_italic,
        font_color: DEFAULT_NFC_TYPOGRAPHY.font_color,
        text_shadow: DEFAULT_NFC_TYPOGRAPHY.text_shadow,
        text_shadow_blur: DEFAULT_NFC_TYPOGRAPHY.text_shadow_blur,
        ...options,
    })
}
